// The app OWNS its theme token VALUES (the master-plan contract: "the app
// supplies a theme object → CSS variables; geoai2 supplies its own"). cad-react
// only defines the --cad-* CONTRACT + fallbacks; here we produce the concrete
// values the standalone app applies.
//
// This module is intentionally PURE (no @cad-platform/cad-react import) so it can
// live in the main bundle and run pre-paint (the flash-free hint) WITHOUT pulling
// konva/the engine in. The on-canvas palette (Konva colours) is themed separately
// by the lazy editor chunk (see cad/canvasTheme.ts).

import type { CustomTheme, FontFamilyKey, Preferences, PreferencesOut } from '../api/types';

// Curated font whitelist — KEYS map to vetted stacks (never raw CSS/URLs). Must
// stay in lockstep with the backend's FontFamily Literal. The "Studio" identity
// (C0-R) leads sans/mono with the self-hosted @fontsource variable faces (bundled
// same-origin, CSP-safe) and falls back to system stacks if the font fails.
export const FONT_STACKS: Record<FontFamilyKey, string> = {
  sans: "'Geist Variable', ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  serif: 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
  mono: "'Geist Mono Variable', ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
};

// The "Studio" signature accent — electric violet. One brand accent across light
// AND dark (cohesive branding); white passes AA (≈4.8:1) on this fill. The token
// bridge propagates it to the shadcn `primary` AND (via resolveAccent) the canvas
// selection in every mode.
export const STUDIO_ACCENT = '#6e56f8';

export const FONT_OPTIONS: { key: FontFamilyKey; label: string }[] = [
  { key: 'sans', label: 'Sans' },
  { key: 'serif', label: 'Serif' },
  { key: 'mono', label: 'Mono' },
];

// Interface-size multiplier bounds + default (mirror the backend UiPref clamp).
// The default is "Large" (1.15): the old implicit 1.0 read as too small on big
// displays. The named presets the Settings UI exposes; the slider is continuous.
export const UI_SCALE_MIN = 0.75;
export const UI_SCALE_MAX = 1.6;
export const UI_SCALE_DEFAULT = 1.15;
export const UI_SCALE_PRESETS: { key: string; label: string; value: number }[] = [
  { key: 'compact', label: 'Compact', value: 0.9 },
  { key: 'default', label: 'Default', value: 1.0 },
  { key: 'large', label: 'Large', value: 1.15 },
  { key: 'xl', label: 'Extra-Large', value: 1.35 },
];

/** Clamp a stored/edited scale into the supported range (defensive: an out-of-range
 *  value from an older/newer client never escapes into the CSS). */
export function clampUiScale(n: number): number {
  if (!Number.isFinite(n)) return UI_SCALE_DEFAULT;
  return n < UI_SCALE_MIN ? UI_SCALE_MIN : n > UI_SCALE_MAX ? UI_SCALE_MAX : n;
}

// Mirrors the backend DEFAULT_PREFERENCES exactly (routers/preferences.py).
// First-time default is LIGHT (not 'system'): a brand-new user with no saved
// preference should land on the light UI regardless of their OS colour-scheme.
// An explicit 'system'/'dark'/'light'/'custom' the user later picks is persisted
// and still wins over this default.
export const DEFAULT_PREFERENCES: Preferences = {
  theme: {
    mode: 'light',
    custom: { fontColor: '#e6edf3', accent: STUDIO_ACCENT, background: '#111418', fontFamily: 'sans' },
  },
  editor: {
    units: 'mm',
    gridVisible: true,
    osnap: true,
    autosaveSeconds: 5,
    // [G6m] null = AUTO: the drawn grid chooses a spacing for the zoom level. The snap step is
    // 1 unit, which is what the editor already used — these defaults change nothing for an
    // existing user, they only make the two settings reachable from Settings.
    gridSize: null,
    snapSize: 1,
  },
  ui: { modelSidebarCollapsed: false, uiScale: UI_SCALE_DEFAULT },
  locale: null,
};

// The app's named presets (its OWN values; they happen to match cad-react's
// shipped presets so the default look is identical).
// The three tokens after --cad-font are the C0-R bridge additions (shadcn ring /
// muted surface / danger-contrast). muted-surface values = mix(bg,text,~0.06)
// precomputed (the mix() helper is defined below these literals).
const DARK_CSS: Record<string, string> = {
  '--cad-bg': '#111418',
  '--cad-panel-bg': '#1c2128',
  '--cad-panel-border': 'rgba(255,255,255,0.10)',
  '--cad-text': '#e6edf3',
  '--cad-text-muted': '#8b949e',
  '--cad-accent': STUDIO_ACCENT,
  '--cad-accent-contrast': '#ffffff',
  '--cad-hover': 'rgba(255,255,255,0.08)',
  '--cad-danger': '#f87171',
  '--cad-grid': '#2a2f36',
  '--cad-crosshair': '#9aa4af',
  '--cad-font': FONT_STACKS.sans,
  '--cad-ring': STUDIO_ACCENT,
  // Pen 2 of the dock's icon set — see cad-react/theme/tokens.js. Still its own
  // token, NOT --cad-accent (that is the armed-tool fill, and a glyph drawn in it
  // would vanish exactly when selected), but violet since 2026-09-19. 5.95:1 on
  // this panel; the brand hex manages only 3.35:1 here.
  '--cad-icon-accent': '#a78bfa',
  // Admin growth chart series. Validated (lightness band, chroma, CVD
  // separation, >= 3:1 contrast) against THIS panel colour, not a generic
  // surface. The dark teal is a SELECTED step, not a lightened light one:
  // #5fd4bd reads on a dock button but sits above the mark lightness band.
  '--cad-chart-total': '#6e56f8',
  '--cad-chart-active': '#199e70',
  '--cad-muted-surface': '#1e2125',
  '--cad-danger-contrast': '#ffffff',
  // ── Contract tokens this palette had never supplied ────────────────────────
  // cad-react/theme/tokens.js owns the --cad-* CONTRACT; this file owns the
  // VALUES (see the header). Five tokens had drifted out of this map, so every
  // consumer fell through to the package's FALLBACK — and those fallbacks are
  // the DARK values. On dark that is invisible (they happen to be right); on
  // LIGHT it is the bug: the Object Properties toolbar's dropdowns fell back to
  // #0f1216 and rendered #1f2328 text on a near-black fill, and --cad-warn's
  // light amber washed out on white in the status bar / viewport / PDF panels.
  // Values copied verbatim from tokens.js so the two lists agree.
  '--cad-field-bg': '#0f1216',
  '--cad-focus': '#a5b4fc',
  '--cad-guide': '#818cf8',
  '--cad-warn': '#e3b341',
  '--cad-ok': '#3fb950',
  // A SIXTH token the pass above missed — same defect, same cause, found the same
  // way (a light-theme surface painting itself with the package's dark fallback).
  // Measured live before the fix: getComputedStyle on `.cad-editor` returned NO
  // value for this token, so `bg-[var(--cad-surface-2,#232a33)]` fell through to
  // the dark hex on a white panel. The GIS sidebar's header label came out at
  // 1.09:1 and its body text at 1.76:1 — invisible and unreadable respectively.
  // Three consumers, so this belongs in the palette rather than at any call site:
  // CADGisSidebar (header + notice), docking/CADDockSection (section headers),
  // docking/CADDockSidebar (the icon rail).
  '--cad-surface-2': '#232a33',
};

const LIGHT_CSS: Record<string, string> = {
  '--cad-bg': '#f6f8fa',
  '--cad-panel-bg': '#ffffff',
  '--cad-panel-border': 'rgba(0,0,0,0.12)',
  '--cad-text': '#1f2328',
  // Strengthened from #59636e (~4.7:1) to ~6:1 on white — readable muted text.
  '--cad-text-muted': '#475059',
  '--cad-accent': STUDIO_ACCENT,
  '--cad-accent-contrast': '#ffffff',
  // Was rgba(0,0,0,0.06) — an invisible hover on white. Deepened to a perceptible
  // overlay so chrome hover/active states actually read in light mode.
  '--cad-hover': 'rgba(0,0,0,0.10)',
  '--cad-danger': '#dc2626',
  '--cad-grid': '#d8dee4',
  '--cad-crosshair': '#57606a',
  '--cad-font': FONT_STACKS.sans,
  '--cad-ring': STUDIO_ACCENT,
  '--cad-icon-accent': '#5b3fd6', // 6.72:1 on white — see the dark preset
  '--cad-chart-total': '#6e56f8',
  '--cad-chart-active': '#0a8168',
  '--cad-muted-surface': '#edeff2',
  '--cad-danger-contrast': '#ffffff',
  // The light half of the five contract tokens this map had been missing (see
  // the dark palette above for what their absence cost). Contrast ratios are
  // tokens.js's own measurements against the light sheet / a white panel.
  '--cad-field-bg': '#ffffff',
  '--cad-focus': '#4f46e5', // 6.29:1
  '--cad-guide': '#4f46e5', // 5.91:1 on the light sheet, 6.29:1 on a white panel
  '--cad-warn': '#8a5a00', // 5.93:1
  '--cad-ok': '#1a7f37', // 5.08:1
  // The light half of the sixth token (see the dark palette for what its absence
  // cost). Value copied verbatim from tokens.js so the two lists agree — a subtle
  // grey that separates a header strip from the white panel without becoming a
  // second panel colour. --cad-text on it is ~14:1; --cad-text-muted ~5.6:1.
  '--cad-surface-2': '#f3f5f7',
};

// ── tiny colour maths (so custom themes derive a coherent token set) ───────────

function clamp(n: number): number {
  return n < 0 ? 0 : n > 255 ? 255 : Math.round(n);
}

function parseHex(hex: string): [number, number, number] {
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return '#' + [r, g, b].map((v) => clamp(v).toString(16).padStart(2, '0')).join('');
}

/** Mix `a` toward `b` by fraction `t` (0..1). Concrete hex out (Konva-safe). */
export function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  return toHex([ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t]);
}

export function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = parseHex(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Relative luminance 0..1 (sRGB, gamma-aware). */
export function luminance(hex: string): number {
  const lin = parseHex(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  // 0.0722 for blue, not 0.2152 — that was 0.7152's tail pasted into the blue
  // slot, and the three coefficients summed to 1.143 instead of 1. It skewed
  // every blue-ish colour LIGHT: #a0a0ff scored 0.537 (hence `isLight`, hence a
  // light preset) where the true value is 0.394. It reaches beyond this file:
  // SettingsPage's a11y contrast warning is built on this number.
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

/** WCAG contrast ratio between two hex colours. */
export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// The icon set's second pen and the two chart series, as validated candidates
// for a CUSTOM theme to choose between.
const PEN_CANDIDATES: Record<string, readonly string[]> = {
  // Violet first (the shipped look), then the two teals as RESCUES. The teals
  // are not leftovers: `theme.test.ts` asserts every pen clears 3:1 with
  // '#6e56f8' itself as the background, and violet-on-violet is ~1:1 — so
  // without a non-violet candidate `pickPen` would fall through to the tonal
  // text pen and the two-pen grammar would collapse to monochrome on exactly
  // the background a violet-loving user is most likely to choose.
  '--cad-icon-accent': ['#5b3fd6', '#a78bfa', '#0a8168', '#5fd4bd'],
  '--cad-chart-active': ['#0a8168', '#199e70'],
  '--cad-chart-total': ['#6e56f8', '#8b78ff'],
};

/**
 * Pick the validated candidate that reads best on `panel`; where neither clears
 * 3:1 — a backdrop sitting on top of both hues — fall back to the theme's own
 * TEXT colour.
 *
 * The fallback was `mix(text, panel, 0.35)` first, and a test of a violet
 * backdrop caught it at 2.23:1: mixing a third of the way toward the panel is
 * exactly the wrong direction when the problem is being too close to the panel.
 * Falling back to `text` degrades the second pen from a chromatic distinction
 * to a TONAL one (the ink pen is --cad-text-muted), which is legible where a
 * hue is not, and is the honest thing to do rather than keeping a colour that
 * has stopped being visible.
 */
function pickPen(token: string, panel: string, text: string): string {
  let best = PEN_CANDIDATES[token][0];
  let bestRatio = 0;
  for (const c of PEN_CANDIDATES[token]) {
    const r = contrastRatio(c, panel);
    if (r > bestRatio) {
      bestRatio = r;
      best = c;
    }
  }
  return bestRatio >= 3 ? best : text;
}

function isHexColor(v: string): boolean {
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v);
}

// Derive the full --cad-* set from the four custom knobs. Works for a dark OR a
// light chosen background (every derivation is relative to bg↔text contrast).
function customCss(custom: CustomTheme): { css: Record<string, string>; isLight: boolean } {
  const bg = isHexColor(custom.background) ? custom.background : '#111418';
  const text = isHexColor(custom.fontColor) ? custom.fontColor : '#e6edf3';
  const accent = isHexColor(custom.accent) ? custom.accent : '#4f46e5';
  const isLight = luminance(bg) > 0.5;
  const muted = mix(text, bg, 0.4);
  const danger = isLight ? '#dc2626' : '#f87171';
  // Named, because the pen picker has to measure against it.
  const panel = mix(bg, text, 0.1); // panel lifts a step toward the text
  const css: Record<string, string> = {
    '--cad-bg': bg,
    '--cad-panel-bg': panel,
    '--cad-panel-border': withAlpha(text, 0.16),
    '--cad-text': text,
    '--cad-text-muted': muted,
    '--cad-accent': accent,
    '--cad-accent-contrast': luminance(accent) > 0.5 ? '#111111' : '#ffffff',
    // A touch stronger than before so the hover overlay stays perceptible whether
    // the custom backdrop is light or dark.
    '--cad-hover': withAlpha(text, 0.12),
    '--cad-danger': danger,
    '--cad-grid': mix(bg, text, 0.16),
    '--cad-crosshair': muted,
    '--cad-font': FONT_STACKS[custom.fontFamily] ?? FONT_STACKS.sans,
    // C0-R bridge additions — derived from the same four knobs so shadcn chrome
    // stays coherent under a custom theme.
    '--cad-ring': accent,
    // Never derived from the custom ACCENT — that is an unvalidated colour and
    // can land outside the mark lightness band, or collide with the other
    // series under CVD. Chosen instead from validated candidates by measured
    // contrast against THIS theme's own panel; see PEN_CANDIDATES. The earlier
    // version keyed off `isLight` alone, which guarded against a teal ACCENT
    // and missed the actual hole: a teal BACKGROUND. Measured on it, a custom
    // background of #3fb8a2 put the icon accent at 1.60:1 on the panel — the
    // second pen simply gone, and with it every distinction the icon set
    // exists to draw (FILLET vs CHAMFER, TRIM vs EXTEND, MOVE vs COPY).
    '--cad-icon-accent': pickPen('--cad-icon-accent', panel, text),
    '--cad-chart-total': pickPen('--cad-chart-total', panel, text),
    '--cad-chart-active': pickPen('--cad-chart-active', panel, text),
    '--cad-muted-surface': mix(bg, text, 0.06),
    '--cad-danger-contrast': luminance(danger) > 0.5 ? '#111111' : '#ffffff',
  };
  return { css, isLight };
}

/** The concrete accent hex for the current prefs (Konva-safe). Single source of
 *  truth for "the brand accent right now" — used by the chrome (via --cad-accent)
 *  AND the canvas selection (canvasTheme.ts) so they always match, in every mode. */
export function resolveAccent(prefs: Preferences): string {
  const { mode, custom } = prefs.theme;
  if (mode === 'custom') return isHexColor(custom.accent) ? custom.accent : STUDIO_ACCENT;
  return STUDIO_ACCENT; // light/dark/system share the one signature accent
}

export function prefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : true;
}

/** Resolve a Preferences object to its concrete --cad-* token map + light flag. */
export function buildCss(
  prefs: Preferences,
  systemPrefersDark: boolean,
): { css: Record<string, string>; isLight: boolean } {
  const mode = prefs.theme.mode;
  if (mode === 'custom') return customCss(prefs.theme.custom);
  const resolved = mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode;
  return resolved === 'light'
    ? { css: LIGHT_CSS, isLight: true }
    : { css: DARK_CSS, isLight: false };
}

/** Apply a token map to the document root (DOM chrome themes from these). */
export function applyCss(css: Record<string, string>, isLight: boolean): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  for (const [k, v] of Object.entries(css)) el.style.setProperty(k, v);
  el.style.colorScheme = isLight ? 'light' : 'dark';
}

/** One-shot: resolve prefs against the live media query and apply. Returns the
 *  css map (for the localStorage hint). The interface-size multiplier rides along
 *  as `--cad-ui-scale`; index.css multiplies the viewport-adaptive root font-size
 *  by it, so the whole UI (shell + editor chrome) scales from this one token. */
export function applyPreferences(prefs: Preferences): Record<string, string> {
  const { css, isLight } = buildCss(prefs, prefersDark());
  const withScale = {
    ...css,
    '--cad-ui-scale': String(clampUiScale(prefs.ui?.uiScale ?? UI_SCALE_DEFAULT)),
  };
  applyCss(withScale, isLight);
  return withScale;
}

// ── localStorage theme HINT (flash-free boot) — NON-sensitive render hint only ──
const HINT_KEY = 'cadThemeHint';

export function writeThemeHint(css: Record<string, string>): void {
  try {
    localStorage.setItem(HINT_KEY, JSON.stringify(css));
  } catch {
    /* private mode / quota — the hint is best-effort */
  }
}

export function clearThemeHint(): void {
  try {
    localStorage.removeItem(HINT_KEY);
  } catch {
    /* ignore */
  }
}

/** Pre-paint: apply the last-known token map so first paint matches the saved
 *  theme (no flash). Render hint ONLY — authoritative prefs load from the server
 *  right after and overwrite this. Pure DOM; safe in the main bundle. */
export function applyThemeHint(): void {
  try {
    const raw = localStorage.getItem(HINT_KEY);
    if (!raw) return;
    const css = JSON.parse(raw) as Record<string, unknown>;
    const el = document.documentElement;
    for (const [k, v] of Object.entries(css)) {
      if (k.startsWith('--cad-') && typeof v === 'string') el.style.setProperty(k, v);
    }
    const bg = css['--cad-bg'];
    if (typeof bg === 'string' && isHexColor(bg)) {
      el.style.colorScheme = luminance(bg) > 0.5 ? 'light' : 'dark';
    }
  } catch {
    /* malformed hint — ignore, the server load will set things right */
  }
}

/** Drop server metadata to the PUT-able Preferences shape. `ui` is defaulted so an
 *  older server (rolling deploy) or a pre-C0-R stored blob — which has no `ui` —
 *  can never leave `prefs.ui` undefined and crash the shell. */
export function toPreferences(out: PreferencesOut): Preferences {
  return {
    theme: out.theme,
    editor: out.editor,
    // Merge over defaults so a blob from an older server (no `ui`, or a `ui`
    // without `uiScale`) can never leave a field undefined and break the shell.
    ui: { ...DEFAULT_PREFERENCES.ui, ...(out.ui ?? {}) },
    locale: out.locale,
  };
}
